import { PLATFORM_BASE_URL } from "../platform/config";
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  SimpleGrid,
  Stack,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import React from "react";
import {
  OktonLoadingState,
  OktonPageHeader,
  OktonPanel,
  useOktonAuth,
  useOktonPlatform,
} from "@okton/platform-ui";

interface ApplicationSetting {
  id: string;
  name: string;
  features: {
    billing: boolean;
    profile: boolean;
    branchSwitching: boolean;
    notifications: boolean;
  };
  defaultAllowed: boolean | null;
}

interface SettingsPayload {
  preferences: Record<string, unknown>;
  applications: ApplicationSetting[];
}

export const SettingsPage: React.FC = () => {
  const { accessToken } = useOktonAuth();
  const { client, refreshPreferences } = useOktonPlatform();
  const [data, setData] = React.useState<SettingsPayload>({
    preferences: {},
    applications: [],
  });
  const [compactMode, setCompactMode] = React.useState(false);
  const [showDescriptions, setShowDescriptions] = React.useState(true);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [message, setMessage] = React.useState("");
  const [error, setError] = React.useState("");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  React.useEffect(() => {
    let active = true;

    async function load(): Promise<void> {
      if (!accessToken) return;
      setLoading(true);
      setError("");

      try {
        const response = await fetch(`${PLATFORM_BASE_URL}/v1/admin/settings`, {
          headers: { Authorization: `Bearer ${accessToken}` },
          credentials: "include",
        });
        const payload = await response.json();
        if (!response.ok)
          throw new Error(
            payload.message || "Não foi possível carregar as configurações.",
          );
        if (!active) return;

        const loaded = payload.data || { preferences: {}, applications: [] };
        setData(loaded);
        setCompactMode(Boolean(loaded.preferences?.compactMode));
        setShowDescriptions(loaded.preferences?.showDescriptions !== false);
      } catch (err) {
        if (active)
          setError(
            err instanceof Error
              ? err.message
              : "Erro ao carregar configurações.",
          );
      } finally {
        if (active) setLoading(false);
      }
    }

    load();
    return () => {
      active = false;
    };
  }, [accessToken]);

  async function savePreferences(): Promise<void> {
    if (!accessToken) return;
    setSaving(true);
    setMessage("");
    setError("");

    try {
      const preferences = {
        ...data.preferences,
        compactMode,
        showDescriptions,
      };
      if (!client?.savePreferences)
        throw new Error("Cliente da plataforma não permite salvar preferências.");

      const savedPreferences = await client.savePreferences(preferences);
      setData((current) => ({
        ...current,
        preferences: savedPreferences,
      }));
      await refreshPreferences();
      setMessage("Preferências salvas.");
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Erro ao salvar preferências.",
      );
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <OktonLoadingState title="Carregando configurações..." minH="50vh" />
    );
  }

  return (
    <Box px={[4, 6, 9]} py={[6, 8, 16]} w="100%">
      <OktonPageHeader
        title="Configurações"
        subtitle="Preferências pessoais e visão das capacidades da plataforma."
      />

      {error && (
        <Text color="red.500" mb="4">
          {error}
        </Text>
      )}
      {message && (
        <Text color="green.500" mb="4">
          {message}
        </Text>
      )}

      <SimpleGrid columns={[1, 2]} spacing="6">
        <OktonPanel title="Preferências pessoais">
          <Stack spacing="4">
            <Checkbox
              isChecked={compactMode}
              onChange={(e) => setCompactMode(e.target.checked)}
            >
              Interface compacta
            </Checkbox>
            <Checkbox
              isChecked={showDescriptions}
              onChange={(e) => setShowDescriptions(e.target.checked)}
            >
              Exibir descrições auxiliares
            </Checkbox>
            <Button
              colorScheme="blue"
              alignSelf="flex-start"
              onClick={savePreferences}
              isLoading={saving}
            >
              Salvar preferências
            </Button>
          </Stack>
        </OktonPanel>

        <OktonPanel title="Capacidades dos sistemas">
          <Stack spacing="4">
            {data.applications.map((app) => (
              <Box
                key={app.id}
                borderBottomWidth="1px"
                borderColor={borderColor}
                pb="4"
              >
                <Flex justify="space-between" align="center" mb="2">
                  <Box>
                    <Text fontWeight="600">{app.name}</Text>
                    <Text fontSize="xs" color="gray.500">
                      {app.id}
                    </Text>
                  </Box>
                  <Badge
                    colorScheme={
                      app.defaultAllowed === false
                        ? "red"
                        : app.defaultAllowed === true
                          ? "green"
                          : "gray"
                    }
                  >
                    {app.defaultAllowed === false
                      ? "Restrito"
                      : app.defaultAllowed === true
                        ? "Padrão liberado"
                        : "Sem política"}
                  </Badge>
                </Flex>
                <Flex wrap="wrap" gap="2">
                  <Badge colorScheme={app.features.profile ? "green" : "gray"}>
                    Perfil
                  </Badge>
                  <Badge colorScheme={app.features.billing ? "green" : "gray"}>
                    Faturamento
                  </Badge>
                  <Badge
                    colorScheme={
                      app.features.branchSwitching ? "green" : "gray"
                    }
                  >
                    Troca de filial
                  </Badge>
                  <Badge
                    colorScheme={app.features.notifications ? "green" : "gray"}
                  >
                    Notificações
                  </Badge>
                </Flex>
              </Box>
            ))}
          </Stack>
        </OktonPanel>
      </SimpleGrid>
    </Box>
  );
};
